<template>
  <div class="page-container reveal">
    <!-- ═══ LIST VIEW ═══ -->
    <el-card v-if="!selected" class="main-list-card">
      <div class="list-header">
        <div class="header-content">
          <h2 class="page-title">Supervision des Missions</h2>
          <p class="page-subtitle">{{ rows.length }} intervention{{ rows.length !== 1 ? 's' : '' }} en cours ou passées</p>
        </div>
        <div class="header-actions">
          <el-input
            v-model="searchQ"
            placeholder="Artisan, client, téléphone..."
            class="w-64"
            @input="load"
          >
            <template #prefix><el-icon><Search /></el-icon></template>
          </el-input>

          <el-select v-model="statusFilter" class="w-48" @change="load">
            <el-option label="Tous les statuts" value="" />
            <el-option label="Proposée" value="PROPOSED" />
            <el-option label="Acceptée" value="ACCEPTED" />
            <el-option label="En route" value="EN_ROUTE" />
            <el-option label="Arrivé" value="ARRIVED" />
            <el-option label="Devis en attente" value="QUOTE_PENDING" />
            <el-option label="En cours" value="WORKING" />
            <el-option label="Clôture en attente" value="PENDING_CLOSURE" />
            <el-option label="Terminée" value="DONE" />
            <el-option label="Litige" value="DISPUTED" />
            <el-option label="Annulée" value="CANCELLED" />
          </el-select>

          <el-select v-model="categoryFilter" class="w-48" @change="load">
            <el-option label="Toutes les catégories" value="" />
            <el-option v-for="c in categories" :key="c.id" :label="c.label_fr" :value="c.id" />
          </el-select>

          <el-select v-model="deliveryFilter" class="w-48">
            <el-option label="Toutes livraisons" value="" />
            <el-option label="Avec incident" value="issue" />
            <el-option label="Push sans ACK" value="unacked" />
            <el-option label="Push échoué" value="failed" />
            <el-option label="SMS fallback envoyé" value="sms_fallback" />
          </el-select>

          <el-button :icon="Download" @click="handleExportCsv" title="Exporter les missions en fichier Excel/CSV">
            Exporter CSV
          </el-button>
          <el-button @click="load" :loading="isLoading" circle :icon="Refresh" />
        </div>
      </div>

      <div class="flex gap-2 flex-wrap mb-4">
        <el-tag type="info">Suivies: {{ deliveryCounters.tracked }}</el-tag>
        <el-tag type="danger">Incidents: {{ deliveryCounters.issues }}</el-tag>
        <el-tag type="warning">Sans ACK: {{ deliveryCounters.unacked }}</el-tag>
        <el-tag type="danger">Push échoués: {{ deliveryCounters.failed }}</el-tag>
        <el-tag type="warning">SMS fallback: {{ deliveryCounters.smsFallback }}</el-tag>
      </div>

      <div v-if="selectedRows.length && canManageMissions" class="bulk-bar">
        <span class="bulk-count">{{ selectedRows.length }} sélectionné(s)</span>
        <el-button type="warning" size="small" :loading="bulkLoading" @click="bulkForceClose">Forcer clôture</el-button>
      </div>

      <el-table :data="paged" v-loading="isLoading" class="custom-table" stripe @selection-change="onSelectionChange">
        <el-table-column type="selection" width="45" />
        <el-table-column label="Réf." width="80">
          <template #default="{ $index }">
            <span class="font-display font-bold text-profissa-text-muted">#{{ String($index + 1 + (page - 1) * pageSize).padStart(3, '0') }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Artisan Assigné" min-width="180" sortable :sort-method="(a, b) => (a.artisan_name || '').localeCompare(b.artisan_name || '')">
          <template #default="{ row }">
            <div class="font-bold text-[0.9rem]">{{ row.artisan_name || '—' }}</div>
            <div class="text-xs text-profissa-text-muted font-mono mt-0.5">{{ formatPhone(row.artisan_phone) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Client & Mission" min-width="220" sortable :sort-method="(a, b) => (a.client_name || '').localeCompare(b.client_name || '')">
          <template #default="{ row }">
            <div class="font-bold text-[0.9rem]">{{ row.client_name || '—' }}</div>
            <div class="text-xs text-profissa-text-muted mt-0.5 truncate">{{ truncate(row.description, 40) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="Statut de l'Intervention" width="180" sortable :sort-method="(a, b) => (a.status || '').localeCompare(b.status || '')">
          <template #default="{ row }">
            <el-tag :type="statusTagType(row.status)">{{ statusLabel(row.status) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="Facturation" width="130" sortable :sort-method="(a, b) => (a.final_price || 0) - (b.final_price || 0)">
          <template #default="{ row }">
            <span class="font-bold" :class="row.final_price ? 'text-profissa-text-main' : 'text-profissa-text-dim'">
              {{ row.final_price ? row.final_price.toLocaleString('fr-FR') + ' FCFA' : '—' }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="Enregistrée le" width="140" sortable :sort-method="(a, b) => new Date(a.created_at) - new Date(b.created_at)">
          <template #default="{ row }">
            <span class="text-xs text-profissa-text-muted">{{ fmtDate(row.created_at) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="Santé Notif" width="180">
          <template #default="{ row }">
            <div class="flex gap-1 flex-wrap">
              <el-tag v-if="row.notification_health?.push_failed_count" type="danger" size="small">
                push ko {{ row.notification_health.push_failed_count }}
              </el-tag>
              <el-tag v-if="row.notification_health?.push_unacked_count" type="warning" size="small">
                no ack {{ row.notification_health.push_unacked_count }}
              </el-tag>
              <el-tag v-if="row.notification_health?.sms_fallback_sent_count" type="warning" size="small">
                sms {{ row.notification_health.sms_fallback_sent_count }}
              </el-tag>
              <span
                v-if="!row.notification_health?.has_delivery_issue"
                class="text-xs text-profissa-text-dim"
              >
                {{ row.notification_health?.tracked_count ? 'RAS' : '—' }}
              </span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="Gérer" width="80" fixed="right" align="center">
          <template #default="{ row }">
            <ActionDropdown :actions="rowActions(row)" @action="(key) => handleAction(key, row)" />
          </template>
        </el-table-column>
      </el-table>

      <div class="flex justify-end pt-6 mt-4 border-t border-profissa-glass-border">
        <el-pagination
          v-if="filteredRows.length > pageSize"
          v-model:current-page="page"
          v-model:page-size="pageSize"
          layout="total, prev, pager, next"
          :total="filteredRows.length"
          @size-change="page = 1"
        />
      </div>
    </el-card>

    <!-- ═══ DETAIL VIEW (full page) ═══ -->
    <el-card v-else class="detail-card reveal">
      <div class="detail-header">
        <el-button @click="closeDetail" :icon="ArrowLeft" class="btn-back">Retour</el-button>
        <h2 class="detail-title">Suivi d'Intervention</h2>
        <el-tag :type="statusTagType(selected.status)" size="large" class="uppercase font-bold tracking-wider">{{ statusLabel(selected.status) }}</el-tag>
      </div>

      <div v-if="isLoadingDetail" class="flex items-center justify-center p-12 text-profissa-accent">
        <el-icon class="is-loading text-4xl"><Loading /></el-icon>
      </div>

      <div v-else class="detail-body">
        <div class="grid grid-cols-2 gap-4 mb-6">
          <div class="p-4 rounded-xl bg-white/5 border border-profissa-glass-border">
            <div class="text-xs font-black uppercase tracking-widest text-profissa-accent mb-2">Artisan Prestataire</div>
            <div class="font-bold text-lg leading-tight">{{ detail?.artisan?.full_name || selected.artisan_name || '—' }}</div>
            <div class="text-sm font-mono text-profissa-text-muted">{{ formatPhone(detail?.artisan?.phone || selected.artisan_phone) }}</div>
          </div>
          <div class="p-4 rounded-xl bg-white/5 border border-profissa-glass-border">
            <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted mb-2">Client Demandeur</div>
            <div class="font-bold text-lg leading-tight">{{ detail?.client?.full_name || selected.client_name || '—' }}</div>
            <div class="text-sm font-mono text-profissa-text-muted">{{ formatPhone(detail?.client?.phone || selected.client_phone) }}</div>
            <div v-if="detail?.service_request?.landmark_text" class="text-xs text-profissa-text-dim mt-1 flex items-center gap-1">
              <el-icon><Location /></el-icon> {{ detail.service_request.landmark_text }}
            </div>
          </div>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
          <div class="flex flex-col gap-1">
            <span class="text-xs font-bold text-profissa-text-muted uppercase">Statut</span>
            <div><el-tag :type="statusTagType(selected.status)">{{ statusLabel(selected.status) }}</el-tag></div>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-bold text-profissa-text-muted uppercase">Catégorie Métier</span>
            <span class="font-bold">{{ detail?.service_request?.category || '—' }}</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-bold text-profissa-text-muted uppercase">Prix Diagnostic</span>
            <span class="font-bold">{{ selected.diagnostic_price?.toLocaleString('fr-FR') || '—' }} FCFA</span>
          </div>
          <div class="flex flex-col gap-1">
            <span class="text-xs font-bold text-profissa-text-muted uppercase">Facturation Finale</span>
            <span class="font-black text-profissa-accent text-lg">{{ selected.final_price?.toLocaleString('fr-FR') || '—' }} FCFA</span>
          </div>
          <div v-if="selected.rating" class="flex flex-col gap-1">
            <span class="text-xs font-bold text-profissa-text-muted uppercase">Note Qualité</span>
            <span class="font-bold text-yellow-500 flex items-center gap-1"><el-icon><StarFilled /></el-icon> {{ selected.rating }} / 5</span>
          </div>
          <div v-if="selected.sav_valid_until" class="flex flex-col gap-1">
            <span class="text-xs font-bold text-profissa-text-muted uppercase">Garantie SAV jusqu'au</span>
            <span class="font-bold text-green-500">{{ fmtDate(selected.sav_valid_until) }}</span>
          </div>
        </div>

        <div class="mb-6">
          <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted border-b border-profissa-glass-border pb-2 mb-3">Historique (Timeline)</div>
          <div class="flex flex-col gap-2">
            <div v-for="evt in timeline" :key="evt.label" class="flex items-center gap-3 text-sm">
              <span class="text-lg" :class="evt.done ? 'text-green-500 font-bold' : 'text-profissa-glass-border'">{{ evt.done ? '●' : '○' }}</span>
              <span :class="evt.done ? 'text-profissa-text-main font-bold' : 'text-profissa-text-muted'">{{ evt.label }}</span>
              <span v-if="evt.date" class="text-xs text-profissa-text-dim ml-auto font-mono">{{ fmtDate(evt.date) }}</span>
            </div>
          </div>
        </div>

        <div v-if="detail?.service_request?.description || selected.description" class="mb-6">
          <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted border-b border-profissa-glass-border pb-2 mb-3">Description du problème</div>
          <div class="p-4 rounded-xl bg-white/5 border border-profissa-glass-border text-sm leading-relaxed">
            {{ detail?.service_request?.description || selected.description }}
          </div>
        </div>

        <div v-if="(detail?.service_request?.photo_urls?.length) || (detail?.after_photo_urls?.length)" class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div v-if="detail?.service_request?.photo_urls?.length">
            <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted border-b border-profissa-glass-border pb-2 mb-3">Photos Initiales (Avant)</div>
            <div class="flex flex-wrap gap-2">
              <div v-for="(url, i) in detail.service_request.photo_urls" :key="i" class="block overflow-hidden rounded-lg border border-profissa-glass-border hover:border-profissa-accent transition-colors cursor-pointer">
                <el-image :src="url" :alt="'Avant '+(i+1)" class="w-20 h-20 object-cover" :preview-src-list="detail.service_request.photo_urls" :initial-index="i" :preview-teleported="true" hide-on-click-modal />
              </div>
            </div>
          </div>
          <div v-if="detail?.after_photo_urls?.length">
            <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted border-b border-profissa-glass-border pb-2 mb-3">Preuves d'Achèvement (Après)</div>
            <div class="flex flex-wrap gap-2">
              <div v-for="(url, i) in detail.after_photo_urls" :key="i" class="block overflow-hidden rounded-lg border border-profissa-glass-border hover:border-green-500 transition-colors cursor-pointer">
                <el-image :src="url" :alt="'Après '+(i+1)" class="w-20 h-20 object-cover" :preview-src-list="detail.after_photo_urls" :initial-index="i" :preview-teleported="true" hide-on-click-modal />
              </div>
            </div>
          </div>
        </div>

        <div v-if="detail?.quote?.items?.length || detail?.quote?.labor_price" class="mb-6">
          <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted border-b border-profissa-glass-border pb-2 mb-3">Devis Accepté</div>
          <el-table :data="quoteRows" size="small" class="custom-table w-full">
            <el-table-column label="Prestation / Matériel" prop="description" />
            <el-table-column label="Qté" prop="quantity" width="60" align="center" />
            <el-table-column label="Prix unitaire" width="120" align="right">
              <template #default="{ row }">{{ row.unit_price.toLocaleString('fr-FR') }}</template>
            </el-table-column>
            <el-table-column label="Sous-total" width="130" align="right">
              <template #default="{ row }"><span class="font-bold">{{ row.subtotal.toLocaleString('fr-FR') }}</span></template>
            </el-table-column>
          </el-table>
          <div class="text-right font-black text-lg py-3 mt-2 border-t border-profissa-glass-border">
            Total Validé : <span class="text-profissa-accent">{{ quoteTotal.toLocaleString('fr-FR') }} FCFA</span>
          </div>
        </div>

        <div v-if="detail?.escrow_holds?.length" class="mb-4">
          <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted border-b border-profissa-glass-border pb-2 mb-3">États Financiers (Garanties & Séquestre)</div>
          <div class="flex gap-2 flex-wrap">
            <el-tag v-for="(hold, i) in detail.escrow_holds" :key="i" :type="escrowTagType(hold.status)">
              {{ escrowHoldTypeLabel(hold.type) }} — {{ hold.amount.toLocaleString('fr-FR') }} FCFA ({{ escrowHoldStatusBadge(hold.status).label }})
            </el-tag>
          </div>
        </div>

        <div v-if="detail?.notification_deliveries?.length" class="mb-4">
          <div class="text-xs font-black uppercase tracking-widest text-profissa-text-muted border-b border-profissa-glass-border pb-2 mb-3">Livraison des Notifications</div>
          <div class="flex flex-col gap-3">
            <div
              v-for="notification in detail.notification_deliveries"
              :key="notification.id"
              class="p-4 rounded-xl bg-white/5 border border-profissa-glass-border"
            >
              <div class="flex items-start justify-between gap-4">
                <div>
                  <div class="font-bold text-sm">{{ notification.title }}</div>
                  <div class="text-xs text-profissa-text-dim mt-1">
                    {{ notificationTypeLabel(notification.type) }} ·
                    {{ recipientLabel(notification.recipient) }} ·
                    {{ fmtDate(notification.created_at) }}
                  </div>
                  <div class="text-xs text-profissa-text-muted mt-2">{{ notification.body }}</div>
                </div>
                <div class="flex gap-2 flex-wrap justify-end">
                  <el-tag
                    v-for="delivery in notification.deliveries"
                    :key="`${notification.id}-${delivery.channel}`"
                    :type="deliveryStatusTagType(delivery.status)"
                    class="font-mono"
                  >
                    {{ delivery.channel }} · {{ delivery.status }}
                  </el-tag>
                </div>
              </div>
              <div v-if="notification.deliveries?.length" class="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
                <div
                  v-for="delivery in notification.deliveries"
                  :key="`${notification.id}-${delivery.channel}-detail`"
                  class="rounded-lg border border-profissa-glass-border px-3 py-2 text-xs"
                >
                  <div class="font-bold uppercase tracking-wide">{{ delivery.channel }}</div>
                  <div class="text-profissa-text-muted mt-1">Statut: {{ delivery.status }}</div>
                  <div class="text-profissa-text-muted">Tentatives: {{ delivery.attempt_count }}</div>
                  <div class="text-profissa-text-muted">Envoyé: {{ fmtDate(delivery.sent_at) }}</div>
                  <div class="text-profissa-text-muted">Reçu: {{ fmtDate(delivery.delivered_at) }}</div>
                  <div class="text-profissa-text-muted">Ouvert: {{ fmtDate(delivery.opened_at) }}</div>
                  <div class="text-profissa-text-muted">Fallback SMS: {{ fmtDate(delivery.fallback_sent_at) }}</div>
                  <div v-if="delivery.last_error" class="text-red-400 mt-1">Erreur: {{ delivery.last_error }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="detail-footer">
        <div class="flex gap-2 flex-wrap">
          <el-button @click="$emit('view-chat', selected)" :icon="ChatDotRound">
            Historique du Chat
          </el-button>
          <el-popconfirm
            v-if="canManageMissions && selected?.status === 'PENDING_CLOSURE'"
            title="Forcer la clôture et libérer l'escrow ?"
            confirm-button-text="Confirmer"
            cancel-button-text="Annuler"
            confirm-button-type="warning"
            @confirm="forceClose(selected)"
          >
            <template #reference>
              <el-button type="warning" :loading="isForceClosing" :icon="Warning">Clôture Administrative</el-button>
            </template>
          </el-popconfirm>
          <template v-if="hasHeldEscrow && canManageFinance">
            <el-popconfirm
              title="Libérer tout l'escrow vers l'artisan (commission déduite) ?"
              confirm-button-text="Libérer"
              cancel-button-text="Annuler"
              confirm-button-type="success"
              @confirm="doReleaseEscrow('all')"
            >
              <template #reference>
                <el-button type="success" plain :loading="isEscrowAction">Libérer Escrow</el-button>
              </template>
            </el-popconfirm>
            <el-popconfirm
              title="Rembourser tout l'escrow au client ?"
              confirm-button-text="Rembourser"
              cancel-button-text="Annuler"
              confirm-button-type="danger"
              @confirm="doRefundEscrow"
            >
              <template #reference>
                <el-button type="danger" plain :loading="isEscrowAction">Rembourser Client</el-button>
              </template>
            </el-popconfirm>
          </template>
        </div>
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineEmits } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Search, Refresh, View, ChatDotRound, Warning, Loading, Location, StarFilled, ArrowLeft, Download } from '@element-plus/icons-vue'
import { listInterventions, listCategories, getInterventionDetail, forceCloseIntervention, releaseEscrow, refundEscrow } from '../services/adminApi'
import { extractApiError } from '../api'
import ActionDropdown from './shared/ActionDropdown.vue'
import { useAdminPermissions } from '../composables/useAdminPermissions'
import { formatPhone } from '../utils/format'
import { escrowHoldStatusBadge, escrowHoldTypeLabel, missionStatusBadge } from '../utils/labels'
import { exportToCsv } from '../utils/exportCsv'

const emit = defineEmits(['view-chat'])

const handleExportCsv = () => {
  const columns = [
    { key: 'id', label: 'ID Mission' },
    { key: 'status', label: 'Statut', format: (v) => missionStatusBadge(v).label },
    { key: 'category_name', label: 'Catégorie' },
    { key: 'created_at', label: 'Date Création', format: (v) => v ? new Date(v).toLocaleString('fr-FR') : '' },
    { key: 'client_name', label: 'Client Nom', format: (v, r) => r.client?.full_name || r.client_name || '—' },
    { key: 'client_phone', label: 'Client Téléphone', format: (v, r) => formatPhone(r.client?.phone || r.client_phone) },
    { key: 'artisan_name', label: 'Artisan Nom', format: (v, r) => r.artisan?.full_name || r.artisan_name || '—' },
    { key: 'artisan_phone', label: 'Artisan Téléphone', format: (v, r) => formatPhone(r.artisan?.phone || r.artisan_phone) },
    { key: 'quote_amount', label: 'Montant Devis (FCFA)', format: (v, r) => r.quote?.total_amount || r.quote_amount || 0 },
    { key: 'diagnostic_escrow_status', label: 'Escrow Diagnostic' },
    { key: 'final_escrow_status', label: 'Escrow Travaux' },
  ]
  const dateStr = new Date().toISOString().slice(0, 10)
  exportToCsv(`missions_profissa_${dateStr}.csv`, columns, rows.value)
}

const selectedRows   = ref([])
const rows           = ref([])
const categories     = ref([])
const isLoading      = ref(false)
const isLoadingDetail= ref(false)
const isForceClosing  = ref(false)
const isEscrowAction  = ref(false)
const searchQ        = ref('')
const statusFilter   = ref('')
const categoryFilter = ref('')
const deliveryFilter = ref('')
const selected       = ref(null)
const detail         = ref(null)
const page           = ref(1)
const pageSize       = ref(15)
const route          = useRoute()
const router         = useRouter()
const routeMissionId = computed(() => String(route.params.id || ''))
const { hasPermission } = useAdminPermissions()
const canManageMissions = computed(() => hasPermission('missions.manage.any'))
const canManageFinance = computed(() => hasPermission('finance.manage.any'))

const filteredRows = computed(() => rows.value.filter((row) => matchesDeliveryFilter(row, deliveryFilter.value)))
const paged = computed(() => filteredRows.value.slice((page.value - 1) * pageSize.value, page.value * pageSize.value))
const onSelectionChange = (selection) => { selectedRows.value = selection }
const bulkLoading = ref(false)
const deliveryCounters = computed(() => ({
  tracked: rows.value.filter(r => (r.notification_health?.tracked_count || 0) > 0).length,
  issues: rows.value.filter(r => r.notification_health?.has_delivery_issue).length,
  unacked: rows.value.filter(r => (r.notification_health?.push_unacked_count || 0) > 0).length,
  failed: rows.value.filter(r => (r.notification_health?.push_failed_count || 0) > 0).length,
  smsFallback: rows.value.filter(r => (r.notification_health?.sms_fallback_sent_count || 0) > 0).length,
}))

const bulkForceClose = async () => {
  if (!canManageMissions.value) return
  const pending = selectedRows.value.filter(r => r.status === 'PENDING_CLOSURE')
  if (!pending.length) { ElMessage.warning('Aucune mission en attente de clôture parmi la sélection.'); return }
  try {
    await ElMessageBox.confirm(`Forcer la clôture de ${pending.length} mission(s) ? L'escrow sera libéré automatiquement.`, 'Action groupée', { type: 'error' })
    bulkLoading.value = true
    let ok = 0
    for (const row of pending) { try { await forceCloseIntervention(row.id); ok++ } catch { /* skip */ } }
    ElMessage.success(`${ok} mission(s) clôturée(s).`)
    await load()
  } catch { /* cancelled */ }
  finally { bulkLoading.value = false }
}

const truncate = (v, n = 50) => String(v || '').length > n ? String(v).slice(0, n) + '…' : (v || '')
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'

const STATUS_MAP = {
  PROPOSED: { type: 'info', label: 'Proposition' }, ACCEPTED: { type: 'primary', label: 'Assigné' },
  EN_ROUTE: { type: 'warning', label: 'En Transit' }, ARRIVED: { type: 'warning', label: 'Sur Place' },
  QUOTE_PENDING: { type: 'danger', label: 'Attente Devis' }, WORKING: { type: 'warning', label: 'Travaux en cours' },
  PENDING_CLOSURE: { type: 'danger', label: 'Attente Validation' }, DONE: { type: 'success', label: 'Achevée' },
  DISPUTED: { type: 'danger', label: 'Litige Ouvert' }, CANCELLED: { type: 'info', label: 'Annulée' },
}
const statusTagType = (s) => STATUS_MAP[s]?.type || 'info'
const statusLabel   = (s) => STATUS_MAP[s]?.label || s || '—'
const escrowTagType = (s) => ({ held: 'warning', released: 'success', refunded: 'info' }[s] || 'info')
const deliveryStatusTagType = (s) => ({
  pending: 'warning',
  sent: 'info',
  delivered: 'success',
  opened: 'success',
  failed: 'danger',
  skipped: 'info',
  cancelled: 'info',
}[s] || 'info')
const notificationTypeLabel = (type) => ({
  new_mission: 'Nouvelle mission',
  mission_accepted: 'Mission acceptée',
  mission_payment_confirmed: 'Paiement confirmé',
  artisan_en_route: 'Artisan en route',
  quote_ready: 'Devis disponible',
  quote_accepted: 'Devis accepté',
  pin_ready: 'Code PIN prêt',
  mission_done: 'Mission terminée',
  mission_force_closed: 'Clôture administrative',
  support_dispatch: 'Dispatch support',
}[type] || type || 'Notification')
const recipientLabel = (recipient) => {
  if (!recipient) return 'Destinataire inconnu'
  const role = recipient.role === 'artisan' ? 'Artisan' : recipient.role === 'client' ? 'Client' : 'Utilisateur'
  return `${role} · ${recipient.full_name || recipient.phone || '—'}`
}
const matchesDeliveryFilter = (row, filter) => {
  const health = row.notification_health || {}
  if (!filter) return true
  if (filter === 'issue') return !!health.has_delivery_issue
  if (filter === 'unacked') return (health.push_unacked_count || 0) > 0
  if (filter === 'failed') return (health.push_failed_count || 0) > 0 || (health.sms_fallback_failed_count || 0) > 0
  if (filter === 'sms_fallback') return (health.sms_fallback_sent_count || 0) > 0
  return true
}

const timeline = computed(() => {
  if (!selected.value) return []
  const s = selected.value; const d = detail.value
  return [
    { label: 'Demande Émise',                date: s.created_at,       done: !!s.created_at },
    { label: 'Artisan Assigné',             date: s.booked_at,        done: !!s.booked_at },
    { label: 'Code PIN de Sécurité Généré',           date: s.pin_generated_at, done: !!s.pin_generated_at },
    { label: 'Validation Client & Clôture',date: s.pin_validated_at || d?.pin_validated_at, done: !!(s.pin_validated_at || d?.pin_validated_at) },
  ]
})

const quoteRows = computed(() => {
  if (!detail.value?.quote) return []
  const items = [...(detail.value.quote.items || [])]
  if (detail.value.quote.labor_price) {
    items.push({ description: "Prestation : Main d'œuvre", quantity: 1, unit_price: detail.value.quote.labor_price, subtotal: detail.value.quote.labor_price })
  }
  return items
})

const quoteTotal = computed(() => {
  if (!detail.value?.quote) return 0
  return (detail.value.quote.items || []).reduce((acc, i) => acc + i.subtotal, 0) + (detail.value.quote.labor_price || 0)
})

const rowActions = (row) => {
  const actions = [
    { key: 'view', label: 'Voir le dossier', icon: View },
    { key: 'chat', label: 'Lire les échanges', icon: ChatDotRound },
  ]
  if (row.status === 'PENDING_CLOSURE') {
    if (!canManageMissions.value) return actions
    actions.push({ key: 'force-close', label: 'Forcer la clôture', icon: Warning, danger: true, divided: true })
  }
  return actions
}

const handleAction = async (key, row) => {
  if (key === 'view') {
    openDetail(row)
  } else if (key === 'chat') {
    emit('view-chat', row)
  } else if (key === 'force-close') {
    if (!canManageMissions.value) return
    try {
      await ElMessageBox.confirm(
        "Vous vous apprêtez à forcer la clôture de cette mission. L'escrow sera automatiquement libéré à l'artisan sans la validation PIN du client. Continuer ?",
        'Action Administrative Requise',
        { confirmButtonText: 'Forcer Clôture', cancelButtonText: 'Annuler', type: 'error' }
      )
      await forceClose(row)
    } catch {
      /* cancelled */
    }
  }
}

const load = async () => {
  isLoading.value = true
  try {
    rows.value = await listInterventions(searchQ.value, statusFilter.value, categoryFilter.value)
    page.value = 1
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isLoading.value = false }
}

const openDetail = async (r) => {
  if (!routeMissionId.value && r?.id) {
    await router.push(`/missions/${r.id}`)
    return
  }
  selected.value = r; detail.value = null
  isLoadingDetail.value = true
  try { detail.value = await getInterventionDetail(r.id) }
  catch { /* non-blocking */ }
  finally { isLoadingDetail.value = false }
}

const closeDetail = () => {
  if (routeMissionId.value) {
    router.push('/missions')
    return
  }
  selected.value = null
  detail.value = null
}

const hasHeldEscrow = computed(() =>
  (detail.value?.escrow_holds || []).some(h => h.status === 'held')
)

const doReleaseEscrow = async (mode = 'all') => {
  if (!canManageFinance.value) return
  isEscrowAction.value = true
  try {
    await releaseEscrow(selected.value.id, mode)
    ElMessage.success('Escrow libéré — fonds transférés à l\'artisan.')
    detail.value = await getInterventionDetail(selected.value.id)
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isEscrowAction.value = false }
}

const doRefundEscrow = async () => {
  if (!canManageFinance.value) return
  isEscrowAction.value = true
  try {
    await refundEscrow(selected.value.id)
    ElMessage.success('Escrow remboursé au client.')
    detail.value = await getInterventionDetail(selected.value.id)
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isEscrowAction.value = false }
}

const forceClose = async (r) => {
  if (!canManageMissions.value) return
  isForceClosing.value = true
  try {
    await forceCloseIntervention(r.id)
    ElMessage.success('Fonds libérés et mission clôturée administrativement.')
    await load()
    closeDetail()
  } catch (e) { ElMessage.error(extractApiError(e)) }
  finally { isForceClosing.value = false }
}

onMounted(async () => {
  const [, cats] = await Promise.allSettled([load(), listCategories()])
  if (cats.status === 'fulfilled') categories.value = cats.value
  if (routeMissionId.value) {
    await openDetail({ id: routeMissionId.value })
  }
})
</script>

<style scoped>
.page-container { @apply h-full flex flex-col; }
.main-list-card { @apply flex-1 flex flex-col; }
.list-header { @apply flex justify-between items-end pb-8; }
.page-title { @apply text-2xl font-black mb-1; }
.page-subtitle { @apply text-profissa-text-muted text-sm font-semibold; }
.header-actions { @apply flex gap-3 items-center; }
.bulk-bar { @apply flex items-center gap-3 px-4 py-3 mb-4 rounded-xl; background: rgba(255, 107, 0, 0.06); border: 1px solid rgba(255, 107, 0, 0.15); }
.bulk-count { @apply font-extrabold text-sm text-profissa-accent mr-auto; }

.detail-card { @apply flex-1 flex flex-col overflow-hidden; }
.detail-header { @apply flex items-center gap-4 pb-6 border-b border-profissa-glass-border mb-6; }
.btn-back { @apply font-bold rounded-lg; }
.detail-title { @apply text-2xl font-black flex-1 m-0; }
.detail-body { @apply flex-1 overflow-y-auto pr-2; }
.detail-footer { @apply flex justify-between items-center pt-6 border-t border-profissa-glass-border mt-6; }
</style>
